import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ChevronDown, Search, MessageCircle, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors px-4 -mx-4 rounded-lg"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-muted-foreground leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HelpPage = () => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      title: i18n.language === 'fr' ? 'Premiers pas' : 'Getting Started',
      icon: '🚀',
      faqs: [
        {
          q: i18n.language === 'fr' 
            ? 'Qu\'est-ce que XPerience ?'
            : 'What is XPerience?',
          a: i18n.language === 'fr'
            ? 'XPerience est une plateforme qui connecte des guides locaux passionnés (Explorateurs) avec des voyageurs en quête d\'expériences authentiques. Nous proposons des visites guidées, des ateliers, des retraites bien-être, des événements locaux et des rencontres entre passionnés.'
            : 'XPerience is a platform that connects passionate local guides (Explorers) with travelers seeking authentic experiences. We offer guided tours, workshops, wellness retreats, local events and meetups between enthusiasts.'
        },
        {
          q: i18n.language === 'fr' 
            ? 'Comment créer un compte ?'
            : 'How do I create an account?',
          a: i18n.language === 'fr'
            ? 'Cliquez sur "S\'inscrire", choisissez si vous êtes Voyageur ou Guide, remplissez le formulaire avec votre nom, email et mot de passe, puis acceptez les CGU et la Charte. Vous pouvez aussi vous inscrire avec Google.'
            : 'Click "Sign up", choose if you are a Traveler or Guide, fill in the form with your name, email and password, then accept the Terms and Charter. You can also sign up with Google.'
        },
        {
          q: i18n.language === 'fr' 
            ? 'Quelle est la différence entre Voyageur et Guide ?'
            : 'What is the difference between Traveler and Guide?',
          a: i18n.language === 'fr'
            ? '• Voyageur (Expérimentateur) : Vous réservez des expériences proposées par nos guides.\n• Guide (Explorateur) : Vous créez et proposez des expériences aux voyageurs. L\'inscription est 100% gratuite pour les guides.'
            : '• Traveler (Experimenter): You book experiences offered by our guides.\n• Guide (Explorer): You create and offer experiences to travelers. Registration is 100% free for guides.'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Types d\'expériences' : 'Experience Types',
      icon: '🎯',
      faqs: [
        {
          q: i18n.language === 'fr' 
            ? 'Quels types d\'expériences existe-t-il ?'
            : 'What types of experiences are there?',
          a: i18n.language === 'fr'
            ? '1. Expériences payantes : Le guide fixe son prix, ouvertes à tous les voyageurs.\n2. Rencontres gratuites (Meetups) : Expériences gratuites pour les membres XPerience (abonnement 49€/an).\n3. Événements locaux : Festivals, marchés, fêtes locales - gratuits et ouverts à tous.'
            : '1. Paid experiences: The guide sets their price, open to all travelers.\n2. Free meetups: Free experiences for XPerience members (€49/year subscription).\n3. Local events: Festivals, markets, local celebrations - free and open to everyone.'
        },
        {
          q: i18n.language === 'fr' 
            ? 'Qu\'est-ce que l\'abonnement membre à 49€/an ?'
            : 'What is the €49/year membership?',
          a: i18n.language === 'fr'
            ? 'L\'abonnement membre vous donne accès illimité à toutes les expériences gratuites (rencontres entre passionnés, randonnées, échanges culturels...) proposées par nos guides. Les expériences payantes restent accessibles à tous, membre ou non.'
            : 'The membership gives you unlimited access to all free experiences (meetups between enthusiasts, hikes, cultural exchanges...) offered by our guides. Paid experiences remain accessible to everyone, member or not.'
        },
        {
          q: i18n.language === 'fr' 
            ? 'Comment devenir membre ?'
            : 'How do I become a member?',
          a: i18n.language === 'fr'
            ? 'Rendez-vous sur la page "Devenir membre" depuis votre tableau de bord ou directement sur /membership. Le paiement de 49€ est annuel et sécurisé via Stripe. Vous pouvez annuler à tout moment.'
            : 'Go to the "Become a member" page from your dashboard or directly at /membership. The €49 payment is annual and secured via Stripe. You can cancel anytime.'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Réservations' : 'Bookings',
      icon: '📅',
      faqs: [
        {
          q: i18n.language === 'fr' 
            ? 'Comment réserver une expérience ?'
            : 'How do I book an experience?',
          a: i18n.language === 'fr'
            ? '1. Parcourez les expériences sur la page Explorer ou la carte.\n2. Sélectionnez celle qui vous intéresse.\n3. Choisissez une date disponible et le nombre de participants.\n4. Procédez au paiement sécurisé via Stripe.\n5. Vous recevrez une confirmation et pourrez contacter le guide via la messagerie.'
            : '1. Browse experiences on the Explore page or map.\n2. Select the one that interests you.\n3. Choose an available date and number of participants.\n4. Proceed to secure payment via Stripe.\n5. You will receive a confirmation and can contact the guide via messaging.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment se passe le jour de l\'expérience ?'
            : 'What happens on the day of the experience?',
          a: i18n.language === 'fr'
            ? '1. Le guide démarre l\'expérience depuis son tableau de bord.\n2. Profitez de votre expérience !\n3. Après l\'expérience, vous avez 48h pour confirmer que tout s\'est bien passé.\n4. Une fois confirmé, le guide reçoit son paiement 48h plus tard.\n5. N\'oubliez pas de laisser un avis !'
            : '1. The guide starts the experience from their dashboard.\n2. Enjoy your experience!\n3. After the experience, you have 48h to confirm everything went well.\n4. Once confirmed, the guide receives payment 48h later.\n5. Don\'t forget to leave a review!'
        },
        {
          q: i18n.language === 'fr'
            ? 'Puis-je contacter le guide avant de réserver ?'
            : 'Can I contact the guide before booking?',
          a: i18n.language === 'fr'
            ? 'Oui ! Utilisez le formulaire de contact sur la page de l\'expérience pour poser vos questions au guide avant de réserver. Il pourra vous répondre par email.'
            : 'Yes! Use the contact form on the experience page to ask the guide questions before booking. They can reply by email.'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Paiements & Remboursements' : 'Payments & Refunds',
      icon: '💳',
      faqs: [
        {
          q: i18n.language === 'fr'
            ? 'Quels moyens de paiement acceptez-vous ?'
            : 'What payment methods do you accept?',
          a: i18n.language === 'fr'
            ? 'Nous acceptons les cartes de crédit/débit (Visa, Mastercard, American Express) via notre partenaire sécurisé Stripe. Apple Pay et Google Pay sont également disponibles.'
            : 'We accept credit/debit cards (Visa, Mastercard, American Express) through our secure partner Stripe. Apple Pay and Google Pay are also available.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment fonctionne le système de paiement sécurisé ?'
            : 'How does the secure payment system work?',
          a: i18n.language === 'fr'
            ? 'Votre paiement est retenu par XPerience (système escrow) jusqu\'à ce que vous confirmiez la fin de l\'expérience. Cela vous protège en cas de problème. Le guide reçoit son paiement 48h après votre confirmation.'
            : 'Your payment is held by XPerience (escrow system) until you confirm the end of the experience. This protects you in case of issues. The guide receives payment 48h after your confirmation.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Quelle est la politique d\'annulation ?'
            : 'What is the cancellation policy?',
          a: i18n.language === 'fr'
            ? '• Plus de 7 jours avant : Remboursement 100%\n• 3 à 7 jours avant : Remboursement 50%\n• Moins de 3 jours : Pas de remboursement\n• Annulation par le guide : Remboursement 100% dans tous les cas'
            : '• More than 7 days before: 100% refund\n• 3 to 7 days before: 50% refund\n• Less than 3 days: No refund\n• Cancellation by guide: 100% refund in all cases'
        },
        {
          q: i18n.language === 'fr'
            ? 'Quand suis-je remboursé ?'
            : 'When will I be refunded?',
          a: i18n.language === 'fr'
            ? 'En cas d\'annulation éligible, le remboursement est effectué sous 5-10 jours ouvrés sur votre moyen de paiement original. Vous recevrez un email de confirmation.'
            : 'In case of eligible cancellation, the refund is processed within 5-10 business days to your original payment method. You will receive a confirmation email.'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Pour les Guides' : 'For Guides',
      icon: '🧭',
      faqs: [
        {
          q: i18n.language === 'fr'
            ? 'Comment devenir guide XPerience ?'
            : 'How do I become an XPerience guide?',
          a: i18n.language === 'fr'
            ? '1. Inscrivez-vous en tant que Guide (gratuit)\n2. Acceptez la Charte des Guides et les CGU\n3. Complétez votre profil (photo, bio, langues...)\n4. Configurez vos paiements Stripe Connect\n5. Créez votre première expérience !'
            : '1. Sign up as a Guide (free)\n2. Accept the Guide Charter and Terms\n3. Complete your profile (photo, bio, languages...)\n4. Set up your Stripe Connect payments\n5. Create your first experience!'
        },
        {
          q: i18n.language === 'fr'
            ? 'L\'inscription est-elle vraiment gratuite ?'
            : 'Is registration really free?',
          a: i18n.language === 'fr'
            ? 'Oui ! L\'inscription et la création d\'expériences sont 100% gratuites. XPerience prélève uniquement une commission de 15% sur les expériences payantes réservées. Les expériences gratuites ne génèrent aucun frais pour le guide.'
            : 'Yes! Registration and creating experiences are 100% free. XPerience only takes a 15% commission on paid experiences booked. Free experiences generate no fees for the guide.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Quand et comment suis-je payé ?'
            : 'When and how do I get paid?',
          a: i18n.language === 'fr'
            ? '1. Le voyageur réserve et paie.\n2. Vous réalisez l\'expérience.\n3. Le voyageur confirme (ou automatiquement après 48h sans action).\n4. 48h après confirmation, les fonds sont transférés sur votre compte Stripe.\n5. Stripe transfère ensuite sur votre compte bancaire (2-7 jours selon votre banque).'
            : '1. The traveler books and pays.\n2. You deliver the experience.\n3. The traveler confirms (or automatically after 48h with no action).\n4. 48h after confirmation, funds are transferred to your Stripe account.\n5. Stripe then transfers to your bank account (2-7 days depending on your bank).'
        },
        {
          q: i18n.language === 'fr'
            ? 'Puis-je proposer des expériences gratuites ?'
            : 'Can I offer free experiences?',
          a: i18n.language === 'fr'
            ? 'Absolument ! Vous pouvez créer des "Rencontres" gratuites pour partager votre passion. Ces expériences seront réservées aux membres XPerience (abonnement 49€/an), ce qui garantit des participants engagés et respectueux.'
            : 'Absolutely! You can create free "Meetups" to share your passion. These experiences will be reserved for XPerience members (€49/year subscription), which ensures engaged and respectful participants.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment gérer mes disponibilités ?'
            : 'How do I manage my availability?',
          a: i18n.language === 'fr'
            ? 'Dans votre tableau de bord, vous pouvez :\n• Définir vos jours de disponibilité\n• Bloquer des dates spécifiques\n• Activer/désactiver votre profil temporairement\n• Gérer les demandes de réservation'
            : 'In your dashboard, you can:\n• Set your availability days\n• Block specific dates\n• Temporarily activate/deactivate your profile\n• Manage booking requests'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Sécurité & Confiance' : 'Safety & Trust',
      icon: '🛡️',
      faqs: [
        {
          q: i18n.language === 'fr'
            ? 'Comment XPerience protège-t-il les voyageurs ?'
            : 'How does XPerience protect travelers?',
          a: i18n.language === 'fr'
            ? '• Paiement sécurisé (fonds retenus jusqu\'à confirmation)\n• Système d\'avis et évaluations\n• Charte des Guides obligatoire\n• Système de signalement en cas de problème\n• Possibilité d\'ouvrir un litige dans les 48h\n• Support réactif'
            : '• Secure payment (funds held until confirmation)\n• Review and rating system\n• Mandatory Guide Charter\n• Reporting system in case of issues\n• Ability to open a dispute within 48h\n• Responsive support'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment XPerience protège-t-il les guides ?'
            : 'How does XPerience protect guides?',
          a: i18n.language === 'fr'
            ? '• Charte des Voyageurs obligatoire\n• Droit d\'interrompre une expérience en cas de comportement inapproprié\n• Système de signalement des voyageurs\n• Paiement garanti après confirmation\n• Protection contre les faux litiges'
            : '• Mandatory Traveler Charter\n• Right to interrupt an experience in case of inappropriate behavior\n• Traveler reporting system\n• Guaranteed payment after confirmation\n• Protection against false disputes'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment signaler un problème ?'
            : 'How do I report a problem?',
          a: i18n.language === 'fr'
            ? 'Dans la messagerie avec l\'autre personne, cliquez sur le menu (⋮) puis "Signaler". Choisissez la raison (comportement inapproprié, harcèlement, arnaque, non-présentation, sécurité, autre) et décrivez le problème. Notre équipe examine chaque signalement.'
            : 'In the messaging with the other person, click the menu (⋮) then "Report". Choose the reason (inappropriate behavior, harassment, scam, no-show, safety, other) and describe the issue. Our team reviews each report.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment ouvrir un litige ?'
            : 'How do I open a dispute?',
          a: i18n.language === 'fr'
            ? 'Vous avez 48h après l\'expérience pour ouvrir un litige depuis votre tableau de bord (section Réservations). Décrivez le problème en détail et fournissez des preuves si possible. Notre équipe analysera la situation et prendra une décision équitable.'
            : 'You have 48h after the experience to open a dispute from your dashboard (Bookings section). Describe the issue in detail and provide evidence if possible. Our team will analyze the situation and make a fair decision.'
        }
      ]
    },
    {
      title: i18n.language === 'fr' ? 'Compte & Profil' : 'Account & Profile',
      icon: '👤',
      faqs: [
        {
          q: i18n.language === 'fr'
            ? 'Comment modifier mon profil ?'
            : 'How do I edit my profile?',
          a: i18n.language === 'fr'
            ? 'Connectez-vous, accédez à votre tableau de bord et cliquez sur "Mon profil" ou l\'onglet "Profil" pour modifier vos informations, photo, bio, langues parlées, etc.'
            : 'Log in, go to your dashboard and click "My profile" or the "Profile" tab to edit your information, photo, bio, languages spoken, etc.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment changer mon mot de passe ?'
            : 'How do I change my password?',
          a: i18n.language === 'fr'
            ? 'Allez dans votre tableau de bord > Paramètres > Sécurité. Vous pourrez y modifier votre mot de passe en entrant l\'ancien puis le nouveau.'
            : 'Go to your dashboard > Settings > Security. You can change your password there by entering the old one then the new one.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Comment supprimer mon compte ?'
            : 'How do I delete my account?',
          a: i18n.language === 'fr'
            ? 'Contactez-nous par email à contact@xperience-guides.com avec votre demande de suppression. Assurez-vous de n\'avoir aucune réservation en cours. Nous traiterons votre demande sous 30 jours conformément au RGPD.'
            : 'Contact us by email at contact@xperience-guides.com with your deletion request. Make sure you have no ongoing bookings. We will process your request within 30 days in accordance with GDPR.'
        },
        {
          q: i18n.language === 'fr'
            ? 'Mes données sont-elles protégées ?'
            : 'Is my data protected?',
          a: i18n.language === 'fr'
            ? 'Oui, nous respectons le RGPD. Vos données personnelles sont sécurisées et ne sont jamais vendues à des tiers. Consultez notre Politique de Confidentialité pour plus de détails.'
            : 'Yes, we comply with GDPR. Your personal data is secured and never sold to third parties. See our Privacy Policy for more details.'
        }
      ]
    }
  ];

  // Filter FAQs based on search
  const filteredCategories = faqCategories.map(cat => ({
    ...cat,
    faqs: cat.faqs.filter(faq => 
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.faqs.length > 0);

  return (
    <main className="min-h-screen pt-20 pb-16 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8" />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {i18n.language === 'fr' ? 'Centre d\'aide' : 'Help Center'}
            </h1>
            <p className="text-xl text-white/80 mb-8">
              {i18n.language === 'fr' 
                ? 'Comment pouvons-nous vous aider ?'
                : 'How can we help you?'}
            </p>
            
            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder={i18n.language === 'fr' ? 'Rechercher dans la FAQ...' : 'Search FAQ...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg bg-white text-gray-900"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'Aucun résultat trouvé pour votre recherche.'
                : 'No results found for your search.'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredCategories.map((category, catIndex) => (
              <motion.div
                key={catIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border overflow-hidden"
              >
                <div className="bg-gray-50 px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span>{category.icon}</span>
                    {category.title}
                  </h2>
                </div>
                <div className="px-6 py-2">
                  {category.faqs.map((faq, faqIndex) => {
                    const faqId = `${catIndex}-${faqIndex}`;
                    return (
                      <FAQItem
                        key={faqId}
                        question={faq.q}
                        answer={faq.a}
                        isOpen={openFAQ === faqId}
                        onClick={() => setOpenFAQ(openFAQ === faqId ? null : faqId)}
                      />
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 text-center border border-orange-200"
        >
          <h3 className="text-xl font-semibold mb-2">
            {i18n.language === 'fr' 
              ? 'Vous n\'avez pas trouvé votre réponse ?'
              : 'Didn\'t find your answer?'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {i18n.language === 'fr'
              ? 'Notre équipe est là pour vous aider'
              : 'Our team is here to help'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" asChild>
              <a href="mailto:contact@xperience-guides.com">
                <Mail className="w-4 h-4 mr-2" />
                contact@xperience-guides.com
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default HelpPage;
